import { type PointerEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";

export interface ChartPoint {
  /** Short x-axis label, e.g. "Oct 3". */
  label: string;
  value: number;
}

const HEIGHT = 220;
const PAD = { top: 12, right: 12, bottom: 24, left: 52 };

/** Rounds a step up to 1, 2, 2.5 or 5 times a power of ten so ticks land on round numbers. */
function niceStep(raw: number): number {
  if (raw <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const f = raw / pow;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * pow;
}

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => entry && setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

/**
 * Single-series area chart drawn in SVG: 2px line over a soft fill, recessive grid,
 * crosshair and tooltip on hover. Screen readers get the same data as a hidden table.
 */
export function AreaChart({
  points,
  label,
  formatTick,
  tooltip,
}: {
  points: ChartPoint[];
  /** Accessible name and caption of the data table. */
  label: string;
  formatTick: (value: number) => string;
  /** Tooltip body for point `i`. */
  tooltip: (i: number) => ReactNode;
}) {
  const [wrapRef, width] = useWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);
  const gradientId = useId();

  const n = points.length;
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const step = niceStep(Math.max(...points.map((p) => p.value), 0) / 4);
  const yMax = step * 4;
  const x = (i: number) => PAD.left + (n > 1 ? (i * plotW) / (n - 1) : plotW / 2);
  const y = (v: number) => PAD.top + plotH - (v / yMax) * plotH;

  const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(p.value)}`).join("");
  const area = n > 0 ? `${line}L${x(n - 1)},${y(0)}L${x(0)},${y(0)}Z` : "";
  const ticks = [0, 1, 2, 3, 4].map((k) => k * step);
  // About five evenly spaced x labels, always including both ends.
  const every = Math.max(1, Math.ceil((n - 1) / 4));
  const xLabels = points.map((_, i) => i).filter((i) => i % every === 0 || i === n - 1);
  const xLabelsSpaced = xLabels.filter((i, k) => k === xLabels.length - 1 || n - 1 - i >= every / 2);

  function onPointerMove(e: PointerEvent<SVGRectElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const i = n > 1 ? Math.round(((e.clientX - rect.left) / rect.width) * (n - 1)) : 0;
    setActive(Math.min(n - 1, Math.max(0, i)));
  }

  return (
    <div ref={wrapRef} className="relative" style={{ height: HEIGHT }}>
      {width > 0 && n > 0 && (
        <svg width={width} height={HEIGHT} aria-hidden="true" className="block overflow-visible">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--brand)" stopOpacity="0.18" />
              <stop offset="100%" stopColor="var(--brand)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={PAD.left}
                x2={width - PAD.right}
                y1={y(t)}
                y2={y(t)}
                stroke="var(--line)"
                strokeDasharray={t === 0 ? undefined : "2 3"}
              />
              <text
                x={PAD.left - 8}
                y={y(t)}
                dy="0.32em"
                textAnchor="end"
                className="fill-fg-subtle text-[11px] tabular-nums"
              >
                {formatTick(t)}
              </text>
            </g>
          ))}
          {xLabelsSpaced.map((i) => (
            <text
              key={i}
              x={x(i)}
              y={HEIGHT - 6}
              textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}
              className="fill-fg-subtle text-[11px]"
            >
              {points[i]?.label}
            </text>
          ))}
          <path d={area} fill={`url(#${gradientId})`} />
          <path
            d={line}
            fill="none"
            stroke="var(--brand)"
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {active !== null && (
            <>
              <line
                x1={x(active)}
                x2={x(active)}
                y1={PAD.top}
                y2={y(0)}
                stroke="var(--line-strong)"
                strokeWidth="1"
              />
              <circle
                cx={x(active)}
                cy={y(points[active]?.value ?? 0)}
                r="4"
                fill="var(--brand)"
                stroke="var(--surface)"
                strokeWidth="2"
              />
            </>
          )}
          <rect
            x={PAD.left}
            y={PAD.top}
            width={plotW}
            height={plotH}
            fill="transparent"
            onPointerMove={onPointerMove}
            onPointerLeave={() => setActive(null)}
          />
        </svg>
      )}
      {active !== null && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-0 z-10 rounded-md border border-line bg-surface px-2.5 py-1.5 text-xs whitespace-nowrap shadow-md"
          style={x(active) > width / 2 ? { right: width - x(active) + 10 } : { left: x(active) + 10 }}
        >
          {tooltip(active)}
        </div>
      )}
      {/* sr-only on the wrapper: a <table> ignores the 1px height and would stretch the page. */}
      <div className="sr-only">
        <table>
          <caption>{label}</caption>
          <tbody>
            {points.map((p, i) => (
              <tr key={p.label + String(i)}>
                <td>{tooltip(i)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
